/**
 * FsmPanel — the React Flow FSM mounted inside the real project layout (phase 6d
 * step 2, the cutover). This REPLACES the old Rappid `components/FSM` view.
 *
 * It reproduces the old FSM's chrome and contract exactly so the page looks and
 * behaves identically (6d step 1 proved pixel parity for the canvas itself):
 *
 *  - Renders the SAME `Panel` (className `fsm-view`, type `main`) with the SAME
 *    `fsm-view-header` the old `FSMHeaderView` produced: the "FSM" title, the tool
 *    toolbar (add-initial/add-state/add-final/add-transition + add-FSM /
 *    calculate / copy / check / save-image), and the close button. Below the
 *    header sits the SAME `FsmSelect` object-type/state-machine chooser.
 *  - Routes selection / add-state / add-transition / add-FSM / calculate / copy
 *    through the EXACT confirmable handlers `ProjectVersion` already owns (passed
 *    as props), so the dirty-inspector guard, the add modals, and the report flow
 *    are unchanged. Keyboard del/esc stay owned by ProjectVersion's global
 *    `keyboard` listeners (the old FSM had none of its own).
 *  - Commits state position, transition vertices, and the label position through
 *    the EXISTING uimodel commands (`updateFSMPosition` / `updateFSMVertices` /
 *    `updateFSMLinkLabelPosition`) — the same direct MST writes the old FSM made,
 *    so undo/redo + autosave work for free.
 *  - The check + export buttons drive the SAME root `ui.project` flags
 *    (`setCheckModel` / `setExportFSMImage`) the old FSM used (the export trigger
 *    keeps the 6e PNG-export machinery reachable).
 *
 * The interaction wiring mirrors `EdgPanel`; the FSM-specific twists are the
 * read-only DEFAULT-FSM tool gating (the tool deactivates when the visible FSM
 * becomes non-editable) and the self-transition support (connect onto the same
 * node).
 *
 * Classic JSX runtime (app-wide esbuild transform) needs React in scope.
 */
import React, { useCallback, useEffect } from 'react';
import { observer } from 'mobx-react';
import Panel from '../components/Panel';
import IconButtonJs from '../components/IconButton';
import { Close as CloseIcon } from '../components/Icons';
import { useStores } from '../hooks/useStores';
// useVisibleFSM / useCanCalculateFSM are untyped JS hooks (the model boundary is `any`).
import { useVisibleFSM, useCanCalculateFSM } from '../hooks';
import {
  updateFSMPosition,
  updateFSMVertices,
  updateFSMLinkLabelPosition,
} from '../merode/commands/uimodel';
import FsmFlow from './FsmFlow';
import FsmToolbar from './FsmToolbar';
import FsmSelect from './FsmSelect';
import type { FsmStateType } from './FsmNodes';
import type { Pt } from './edgeGeometry';

// IconButton is an untyped JS component; re-type at the boundary so the header can
// pass the close icon as a child (the props we use: onClick).
const IconButton = IconButtonJs as React.FC<{
  onClick: () => void;
  children?: React.ReactNode;
}>;

export type FsmPanelProps = {
  /** The MERODE model (`version.content`). */
  // The legacy MST stores are untyped .js; `any` is the honest boundary type.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  model: any;
  /** The FSM UI store (`store.ui.fsm`). */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ui: any;
  /** Currently selected element id (`store.ui.selected`). */
  selected: string | null;
  /** Close the FSM panel (the old `requestClose` → `visible.toggleFSMs`). */
  requestClose: () => void;

  // --- ProjectVersion's confirmable handlers (the dirty-inspector guard lives
  // inside these wrappers; the add modals + report flow are owned by
  // ProjectVersion). Signatures match the props the old FSM received. ---

  /**
   * Select an element (confirmable). The wrapper forwards all args to
   * `onSelectBase(id, toggle, openInspector)`; the blank-canvas pane click passes
   * `(fsm.id, false, false)` to re-select the FSM root WITHOUT toggling or opening
   * the inspector (the old Rappid `onPaperClick` contract). Element clicks (from
   * FsmFlow) pass just the id and take the inspector-opening defaults.
   */
  onSelect: (id: string | null, toggle?: boolean, openInspector?: boolean) => void;
  /** Add an FSM to an object type (confirmable). Arg: objectType id. */
  onAddFSM: (objectTypeId: string) => void;
  /** Add a calculated FSM (confirmable). Arg: objectType id. */
  onCalculateFSM: (objectTypeId: string) => void;
  /** Copy the current FSM (confirmable). Args: objectType id, fsm id. */
  onCopyFSM: (objectTypeId: string, fsmId: string) => void;
  /** Select the current FSM for code generation (confirmable). Args: objectType id, fsm id. */
  onSelectForCodeGeneration: (objectTypeId: string, fsmId: string) => void;
  /** Start the add-state flow (confirmable). Args: objectType id, fsm id, type, position. */
  onAddState: (
    objectTypeId: string,
    fsmId: string,
    type: FsmStateType,
    position: { x: number; y: number }
  ) => void;
  /** Start the add-transition flow (confirmable). Args: objectType id, fsm id, source, target. */
  onAddTransition: (
    objectTypeId: string,
    fsmId: string,
    sourceStateId: string,
    targetStateId: string
  ) => void;
};

const FsmPanel = observer(function FsmPanel({
  model,
  ui,
  selected,
  requestClose,
  onSelect,
  onAddFSM,
  onCalculateFSM,
  onCopyFSM,
  onSelectForCodeGeneration,
  onAddState,
  onAddTransition,
}: FsmPanelProps) {
  // The old FSM pulled the export + check handlers off the ROOT store's
  // `ui.project` (distinct from the `ui` prop, which is the per-FSM UIFSM store).
  const { ui: rootUI } = useStores();

  const fsm = useVisibleFSM(model);
  const canCalculateFSM = useCanCalculateFSM(fsm);
  const canEdit = fsm != null && fsm.type !== 'DEFAULT';
  // Read `codegeneration` here in the observer body so the panel re-renders live
  // when the selection flips (the command deselects the sibling FSM too).
  const codeGenerationSelected = fsm != null && fsm.codegeneration === true;
  const fsmView = fsm != null ? model.uimodel.findFSM(fsm) : null;

  // Deactivate the tool whenever the visible FSM becomes read-only (a DEFAULT
  // code-generation FSM). Ported verbatim from the old FSM's `canEdit` effect.
  useEffect(() => {
    if (!canEdit) {
      ui.deactivateTool();
    }
  }, [canEdit, ui]);

  // --- Selection / pane click ------------------------------------------------
  // onSelect is ProjectVersion's confirmable('Select') wrapper. The old FSM's
  // `onElementSelected` selected only when NO tool was active; FsmFlow already
  // gates node/edge clicks on `activeTool == null`, so we route straight through.
  // A blank-canvas click re-selects the FSM ITSELF — the old `onPaperClick` called
  // `onSelect(fsm.id, false, false)` (toggle=false, openInspector=false): select
  // the FSM root WITHOUT opening the FSM inspector and without ever toggling it
  // off. Passing the defaults instead (toggle=true, openInspector=true) regressed
  // this into a flip-flop that popped the inspector on every empty-canvas click.
  const onPaneClick = useCallback(() => {
    if (fsm != null) onSelect(fsm.id, false, false);
  }, [fsm, onSelect]);

  // --- Add state / add transition — ProjectVersion's confirmable flows --------
  const handleAddState = useCallback(
    (type: FsmStateType, position: { x: number; y: number }) => {
      if (fsm == null) return;
      onAddState(fsm.objectType.id, fsm.id, type, position);
    },
    [fsm, onAddState]
  );

  const handleAddTransition = useCallback(
    (sourceStateId: string, targetStateId: string) => {
      if (fsm == null) return;
      onAddTransition(fsm.objectType.id, fsm.id, sourceStateId, targetStateId);
    },
    [fsm, onAddTransition]
  );

  // --- Position / vertices / label — the EXISTING uimodel commands (direct MST
  // writes, exactly like the old FSM committed them itself). FSM-internal. -----
  const onChangePosition = useCallback(
    (stateId: string, x: number, y: number) => {
      if (fsmView == null) return;
      const state = model.metamodel.getStateById(stateId);
      if (state) updateFSMPosition(fsmView, state, { x, y });
    },
    [fsmView, model]
  );

  const onChangeVertices = useCallback(
    (transitionId: string, vertices: Pt[]) => {
      if (fsmView == null) return;
      const transition = model.metamodel.getTransitionById(transitionId);
      if (transition) updateFSMVertices(fsmView, transition, vertices);
    },
    [fsmView, model]
  );

  const onChangeLabelPosition = useCallback(
    (transitionId: string, position: { offset: number; distance: number }) => {
      if (fsmView == null) return;
      const transition = model.metamodel.getTransitionById(transitionId);
      if (transition) updateFSMLinkLabelPosition(fsmView, transition, position);
    },
    [fsmView, model]
  );

  // --- Check + export — the SAME root `ui.project` flags the old FSM used -----
  const onCheckFSM = useCallback(() => {
    if (fsm != null) rootUI.project.setCheckModel(true, fsm.id);
  }, [rootUI, fsm]);

  const onExportImage = useCallback(() => {
    rootUI.project.setExportFSMImage(true);
  }, [rootUI]);

  // --- Header — reproduces `FSMHeaderView` (the SAME `fsm-view-header` markup:
  // title, tool toolbar incl. save-image, close). ----------------------------
  const renderHeader = useCallback(() => {
    const onAddFSMClick = fsm != null ? () => onAddFSM(fsm.objectType.id) : undefined;
    const onCalculateFSMClick =
      canCalculateFSM && fsm != null ? () => onCalculateFSM(fsm.objectType.id) : undefined;
    const onCopyFSMClick = fsm != null ? () => onCopyFSM(fsm.objectType.id, fsm.id) : undefined;
    const onCheckFSMClick = fsm != null ? onCheckFSM : undefined;
    const onSelectForCodeGenerationClick =
      fsm != null ? () => onSelectForCodeGeneration(fsm.objectType.id, fsm.id) : undefined;

    return (
      <div className="fsm-view-header h-full flex justify-center items-center pl-[15px]">
        <div className="title mr-[15px]" title="Finite State Machine">
          FSM
        </div>
        <FsmToolbar
          ui={ui}
          hasFSM={fsm != null}
          canEdit={canEdit}
          onAddFSM={onAddFSMClick}
          onCalculateFSM={onCalculateFSMClick}
          onCopyFSM={onCopyFSMClick}
          onCheckFSM={onCheckFSMClick}
          codeGenerationSelected={codeGenerationSelected}
          onSelectForCodeGeneration={onSelectForCodeGenerationClick}
          onExportImage={onExportImage}
          className="grow mr-[15px]"
        />
        <div className="close-wrapper">
          <IconButton onClick={requestClose}>
            <CloseIcon />
          </IconButton>
        </div>
      </div>
    );
  }, [
    ui,
    fsm,
    canEdit,
    canCalculateFSM,
    codeGenerationSelected,
    onAddFSM,
    onCalculateFSM,
    onCopyFSM,
    onCheckFSM,
    onSelectForCodeGeneration,
    onExportImage,
    requestClose,
  ]);

  return (
    <Panel
      className="fsm-view grow w-[calc(50%-300px)] min-w-[300px]"
      type="main"
      header={renderHeader}
    >
      <FsmSelect model={model} onSelect={onSelect} />
      <div className="fsm-flow">
        {fsmView != null ? (
          <FsmFlow
            fsmView={fsmView}
            highlighted={ui.highlighted}
            interactive
            activeTool={ui.activeTool ?? null}
            onSelect={onSelect}
            onPaneClick={onPaneClick}
            onAddState={handleAddState}
            onAddTransition={handleAddTransition}
            onChangePosition={onChangePosition}
            onChangeVertices={onChangeVertices}
            onChangeLabelPosition={onChangeLabelPosition}
          />
        ) : null}
      </div>
    </Panel>
  );
});

export default FsmPanel;
